import { useEffect, useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  ExternalLinkIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";

import { ConfirmationDialog } from "@/components/ConfirmationDialog";
import { ExploreImageChip } from "@/components/tools/ExploreImageChip";
import {
  JobPreviewSheetPreview,
  MobileFeedPreview,
  SignupHeroPreview,
} from "@/components/tools/ExploreImagePreviews";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import { useDeleteExploreImage } from "@/hooks/mutations/useDeleteExploreImage";
import { useUpdateExploreImage } from "@/hooks/mutations/useUpdateExploreImage";
import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { positionCategoryToLabel } from "@/lib/constants";
import {
  EXPLORE_IMAGE_CAPTION_MAX_LENGTH,
  EXPLORE_IMAGE_KEYWORD_MAX_LENGTH,
  EXPLORE_IMAGE_MAX_KEYWORDS,
  chamferClipPath,
  exploreImageIndustryToLabel,
  formatExploreImageCredit,
  formatExploreImageSize,
  isLowResExploreImage,
  isPortraitExploreImage,
} from "@/lib/exploreImages";
import { cn } from "@/lib/utils";
import {
  ExploreImage,
  ExploreImageFocus,
  ExploreImageIndustry,
  PositionCategory,
} from "@/types";

// The full-width feed card is 358 × 124pt: the tightest crop the app makes,
// so it's the one the stage outlines.
const FEED_CARD_ASPECT_RATIO = 358 / 124;

// Fixed chrome heights, so the stage image can be sized to exactly the space
// left: viewport − top bar (h-14) − stage footer (h-12) − stage padding (py-6).
const STAGE_IMAGE_MAX_HEIGHT = "max-h-[calc(100dvh-152px)]";

const PREVIEW_TABS = [
  { value: "feed", label: "Mobile feed" },
  { value: "sheet", label: "Job preview" },
  { value: "signup", label: "Signup hero" },
  { value: "full", label: "Full size" },
] as const;
type PreviewTab = (typeof PREVIEW_TABS)[number]["value"];

const percent = (value: number) => `${value.toString()}%`;

/** The part of the image the feed card shows for a focus, in percent. */
const feedCropBox = (image: ExploreImage, focus: ExploreImageFocus) => {
  const imageAspectRatio = image.width / image.height;
  if (imageAspectRatio < FEED_CARD_ASPECT_RATIO) {
    const height = (imageAspectRatio / FEED_CARD_ASPECT_RATIO) * 100;
    return {
      left: 0,
      width: 100,
      top: ((100 - height) * focus.y) / 100,
      height,
    };
  }
  const width = (FEED_CARD_ASPECT_RATIO / imageAspectRatio) * 100;
  return { left: ((100 - width) * focus.x) / 100, width, top: 0, height: 100 };
};

const PanelLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="font-plex-mono text-[10px] uppercase tracking-[0.2em] text-[#77787A]">
    {children}
  </span>
);

const PanelLink = ({ children, href }: { children: string; href: string }) => (
  <a
    className="inline-flex items-center gap-1 font-plex-mono text-[11px] uppercase tracking-[0.12em] text-[#D17949] underline-offset-4 hover:underline dark:text-[#F39562]"
    href={href}
    rel="noreferrer"
    target="_blank"
  >
    {children}
    <ExternalLinkIcon className="h-3 w-3" />
  </a>
);

const StageButton = ({
  children,
  className,
  onClick,
  title,
}: {
  children: React.ReactNode;
  className?: string;
  onClick: () => void;
  title: string;
}) => (
  <button
    aria-label={title}
    className={cn(
      "flex h-10 w-10 shrink-0 items-center justify-center bg-black/[0.06] text-[#212327]/80 backdrop-blur transition-colors hover:bg-black/[0.12] hover:text-[#212327] dark:bg-white/[0.06] dark:text-white/80 dark:hover:bg-white/[0.14] dark:hover:text-white",
      className,
    )}
    onClick={onClick}
    style={{ clipPath: chamferClipPath(8) }}
    title={title}
    type="button"
  >
    {children}
  </button>
);

const LightboxBody = ({
  image,
  index,
  onClose,
  onDeleted,
  onNext,
  onPrevious,
  total,
}: {
  image: ExploreImage;
  index: number;
  onClose: () => void;
  onDeleted: () => void;
  onNext: () => void;
  onPrevious: () => void;
  total: number;
}) => {
  /* React Hooks */
  // The body is keyed by image id, so these start fresh for each image.
  const [focus, setFocus] = useState<ExploreImageFocus>(image.focus);
  const [caption, setCaption] = useState(image.caption ?? "");
  const [keywordInput, setKeywordInput] = useState("");
  const [previewTab, setPreviewTab] = useState<PreviewTab>("feed");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  /* Database CRUD Operations */
  const updateExploreImage = useUpdateExploreImage();
  const deleteExploreImage = useDeleteExploreImage();

  const showSuccessToast = useSuccessToast();
  const showErrorToast = useErrorToast();

  // ← / → step through the gallery unless someone is typing.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isDeleteDialogOpen) return;
      if ((e.target as HTMLElement).closest("input, textarea")) return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        onNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        onPrevious();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDeleteDialogOpen, onNext, onPrevious]);

  /* Helpers */
  const isFocusChanged = focus.x !== image.focus.x || focus.y !== image.focus.y;
  const crop = feedCropBox(image, focus);

  /* Handlers */
  const handleStageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFocus({
      x: Math.round(((e.clientX - rect.left) / rect.width) * 100),
      y: Math.round(((e.clientY - rect.top) / rect.height) * 100),
    });
  };

  const handleSaveFocus = () => {
    updateExploreImage.mutate(
      { id: image.id, focus },
      {
        onSuccess: () => {
          showSuccessToast({ description: "Focus saved." });
        },
        onError: () => {
          showErrorToast({ description: "Failed to save focus." });
        },
      },
    );
  };

  const handleSaveCaption = () => {
    const trimmed = caption.trim();
    if (trimmed === (image.caption ?? "")) return;
    updateExploreImage.mutate(
      { id: image.id, caption: trimmed },
      {
        onError: () => {
          showErrorToast({ description: "Failed to save caption." });
        },
      },
    );
  };

  const saveKeywords = (keywords: string[]) => {
    updateExploreImage.mutate(
      { id: image.id, keywords },
      {
        onError: () => {
          showErrorToast({ description: "Failed to save keywords." });
        },
      },
    );
  };

  // Adds whatever is typed, split on commas, with guac's normalization so the
  // chips don't jump after the save.
  const handleAddKeywords = () => {
    const additions = keywordInput
      .split(",")
      .map((keyword) =>
        keyword.trim().toLowerCase().slice(0, EXPLORE_IMAGE_KEYWORD_MAX_LENGTH),
      )
      .filter((keyword) => keyword && !image.keywords.includes(keyword));
    setKeywordInput("");
    if (!additions.length) return;
    const keywords = [...new Set([...image.keywords, ...additions])];
    if (keywords.length > EXPLORE_IMAGE_MAX_KEYWORDS) {
      showErrorToast({
        description: `An image can have at most ${EXPLORE_IMAGE_MAX_KEYWORDS.toString()} keywords.`,
      });
      return;
    }
    saveKeywords(keywords);
  };

  const handleRemoveKeyword = (keyword: string) => {
    saveKeywords(image.keywords.filter((k) => k !== keyword));
  };

  const handleToggleIndustry = (industry: ExploreImageIndustry) => {
    const industries = image.industries.includes(industry)
      ? image.industries.filter((i) => i !== industry)
      : [...image.industries, industry];
    updateExploreImage.mutate(
      { id: image.id, industries },
      {
        onError: () => {
          showErrorToast({ description: "Failed to update industries." });
        },
      },
    );
  };

  const handleToggleCategory = (category: PositionCategory) => {
    const isListed = image.positionCategories.includes(category);
    const positionCategories = isListed
      ? image.positionCategories.filter((c) => c !== category)
      : [...image.positionCategories, category];
    if (!positionCategories.length) {
      showErrorToast({
        description: "An image needs at least one category. Delete it instead.",
      });
      return;
    }
    updateExploreImage.mutate(
      { id: image.id, positionCategories },
      {
        onError: () => {
          showErrorToast({ description: "Failed to update categories." });
        },
      },
    );
  };

  const handleDelete = () => {
    deleteExploreImage.mutate(
      { id: image.id },
      {
        onSuccess: () => {
          setIsDeleteDialogOpen(false);
          onDeleted();
        },
      },
    );
  };

  /* Render */
  const renderPreview = () => {
    if (previewTab === "feed")
      return <MobileFeedPreview focus={focus} url={image.url} />;
    if (previewTab === "sheet")
      return (
        <JobPreviewSheetPreview
          attribution={image.attribution}
          focus={focus}
          url={image.url}
        />
      );
    if (previewTab === "signup")
      return <SignupHeroPreview focus={focus} url={image.url} />;
    // Real pixels, so admins can judge sharpness; scrolls when larger.
    return (
      <div className="max-h-[60vh] overflow-auto ring-1 ring-black/10 dark:ring-white/10">
        <img
          alt=""
          className="max-w-none"
          src={image.url}
          style={{ width: image.width, height: image.height }}
        />
      </div>
    );
  };

  return (
    // Below lg the dialog scrolls: stage fills one screen, panel follows it.
    // From lg up everything fits the viewport and only the panel scrolls.
    <div className="grid min-h-full grid-rows-[auto_auto] lg:h-full lg:grid-rows-[auto_minmax(0,1fr)]">
      <DialogTitle className="sr-only">Explore image {image.id}</DialogTitle>

      {/* Top bar */}
      <div className="flex h-14 items-center gap-4 border-b border-black/10 px-5 dark:border-white/10">
        <span className="font-plex-mono text-xs tracking-[0.16em] text-[#D17949] dark:text-[#F39562]">
          {String(index + 1).padStart(3, "0")}
          <span className="text-[#77787A]">
            {" "}
            / {String(total).padStart(3, "0")}
          </span>
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-[#4C4F52] dark:text-[#DDDDDD]">
          {image.caption ?? (
            <span className="italic text-[#77787A]">No caption</span>
          )}
        </span>
        <button
          className="flex items-center gap-1.5 font-plex-mono text-[11px] tracking-[0.12em] text-[#77787A] hover:text-[#212327] dark:text-[#9E9E9E] dark:hover:text-white"
          onClick={() => {
            void navigator.clipboard.writeText(image.id);
            showSuccessToast({ description: "Image id copied." });
          }}
          title="Copy image id"
          type="button"
        >
          {image.id.slice(0, 8)}
          <CopyIcon className="h-3.5 w-3.5" />
        </button>
        <StageButton onClick={onClose} title="Close (Esc)">
          <XIcon className="h-5 w-5" />
        </StageButton>
      </div>

      <div className="grid lg:min-h-0 lg:grid-cols-[minmax(0,1fr)_480px] lg:grid-rows-[minmax(0,1fr)]">
        {/* Stage: click to set the focus. A neutral backdrop in light mode,
            carbon in dark, so the photo reads true either way. */}
        <div className="relative flex h-[calc(100dvh-56px)] flex-col bg-[radial-gradient(ellipse_at_center,#F0F0F0_0%,#DDDDDD_75%)] dark:bg-[radial-gradient(ellipse_at_center,#1C1D21_0%,#0E0F11_70%)] lg:h-auto lg:min-h-0">
          <div className="flex min-h-0 flex-1 items-center justify-center px-16 py-6">
            <div
              className="relative inline-block cursor-crosshair select-none overflow-hidden shadow-[0_20px_50px_-20px_rgba(0,0,0,0.5)]"
              onClick={handleStageClick}
            >
              <img
                alt={image.caption ?? ""}
                className={cn("block max-w-full", STAGE_IMAGE_MAX_HEIGHT)}
                draggable={false}
                src={image.url}
              />
              {/* Dims everything the full-width feed card crops away. */}
              <div
                className="pointer-events-none absolute border border-dashed border-[#F39562]/80 shadow-[0_0_0_9999px_rgba(14,15,17,0.55)] transition-all duration-200"
                style={{
                  left: percent(crop.left),
                  top: percent(crop.top),
                  width: percent(crop.width),
                  height: percent(crop.height),
                }}
              >
                <span className="absolute left-0 top-0 bg-[#F39562] px-1.5 py-0.5 font-plex-mono text-[9px] uppercase tracking-[0.16em] text-[#212327]">
                  Feed crop
                </span>
              </div>
              {/* Crosshair guides through the focus. */}
              <div
                className="pointer-events-none absolute inset-y-0 w-px bg-[#F39562]/60"
                style={{ left: percent(focus.x) }}
              />
              <div
                className="pointer-events-none absolute inset-x-0 h-px bg-[#F39562]/60"
                style={{ top: percent(focus.y) }}
              />
              <div
                className="pointer-events-none absolute h-7 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-[#D17949]/50 shadow-[0_0_0_4px_rgba(209,121,73,0.25)]"
                style={{ left: percent(focus.x), top: percent(focus.y) }}
              />
            </div>
          </div>

          <StageButton
            className="absolute left-3 top-1/2 -translate-y-1/2"
            onClick={onPrevious}
            title="Previous (←)"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </StageButton>
          <StageButton
            className="absolute right-3 top-1/2 -translate-y-1/2"
            onClick={onNext}
            title="Next (→)"
          >
            <ChevronRightIcon className="h-6 w-6" />
          </StageButton>

          <div className="flex h-12 shrink-0 items-center gap-3 border-t border-black/10 bg-[#FAFAFA] px-5 dark:border-white/10 dark:bg-[#0E0F11]">
            <span className="shrink-0 font-plex-mono text-[11px] uppercase tracking-[0.16em] text-[#77787A] dark:text-[#9E9E9E]">
              Focus{" "}
              <span className="text-[#212327] dark:text-white">
                {focus.x} · {focus.y}
              </span>
            </span>
            <span className="hidden min-w-0 truncate text-xs text-[#77787A] xl:inline">
              Click the main subject. Every crop in the app keeps it in view.
            </span>
            <div className="ml-auto flex shrink-0 gap-2">
              <button
                className="px-3 py-1.5 font-plex-mono text-[11px] uppercase tracking-[0.12em] text-[#77787A] transition-colors hover:text-[#212327] disabled:opacity-30 dark:text-[#9E9E9E] dark:hover:text-white"
                disabled={!isFocusChanged}
                onClick={() => {
                  setFocus(image.focus);
                }}
                type="button"
              >
                Reset
              </button>
              <button
                className="bg-[#D17949] px-4 py-1.5 font-plex-mono text-[11px] uppercase tracking-[0.12em] text-[#FFF5F0] transition-colors hover:bg-[#F39562] disabled:bg-black/10 disabled:text-black/30 dark:disabled:bg-white/10 dark:disabled:text-white/30"
                disabled={!isFocusChanged || updateExploreImage.isPending}
                onClick={handleSaveFocus}
                style={{ clipPath: chamferClipPath(6) }}
                type="button"
              >
                Save focus
              </button>
            </div>
          </div>
        </div>

        {/* Side panel */}
        <div className="flex flex-col gap-7 border-t border-black/10 bg-white p-6 dark:border-white/10 dark:bg-[#141518] lg:min-h-0 lg:overflow-y-auto lg:border-l lg:border-t-0">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <PanelLabel>Caption</PanelLabel>
              <span className="font-plex-mono text-[10px] text-[#77787A]">
                {caption.length}/{EXPLORE_IMAGE_CAPTION_MAX_LENGTH}
              </span>
            </div>
            <textarea
              className="min-h-[72px] resize-none bg-black/[0.03] px-3 py-2 text-sm text-[#212327] outline-none ring-1 ring-inset ring-black/10 transition-shadow placeholder:text-[#9E9E9E] focus:ring-[#D17949] dark:bg-white/[0.04] dark:text-white dark:ring-white/10 dark:placeholder:text-[#77787A] dark:focus:ring-[#D17949]"
              maxLength={EXPLORE_IMAGE_CAPTION_MAX_LENGTH}
              onBlur={handleSaveCaption}
              onChange={(e) => {
                setCaption(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              placeholder="Welder in a hood, sparks, dark shop…"
              value={caption}
            />
            <span className="text-[11px] text-[#77787A]">
              Searchable. Saves when you press Enter or click away.
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <PanelLabel>Categories</PanelLabel>
            <div className="flex flex-wrap gap-1.5">
              {Object.values(PositionCategory).map((category) => (
                <ExploreImageChip
                  key={category}
                  label={positionCategoryToLabel[category]}
                  onClick={() => {
                    handleToggleCategory(category);
                  }}
                  tone={
                    image.positionCategories.includes(category)
                      ? "active"
                      : "outline"
                  }
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <PanelLabel>Keywords</PanelLabel>
              <span className="font-plex-mono text-[10px] text-[#77787A]">
                {image.keywords.length}/{EXPLORE_IMAGE_MAX_KEYWORDS}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 bg-black/[0.03] p-2 ring-1 ring-inset ring-black/10 focus-within:ring-[#D17949] dark:bg-white/[0.04] dark:ring-white/10 dark:focus-within:ring-[#D17949]">
              {image.keywords.map((keyword) => (
                <span
                  key={keyword}
                  className="inline-flex items-center gap-1 bg-black/[0.06] py-0.5 pl-2 pr-1 font-plex-mono text-[11px] lowercase tracking-[0.04em] text-[#212327] dark:bg-white/[0.08] dark:text-[#EAEAEA]"
                  style={{ clipPath: chamferClipPath(4) }}
                >
                  {keyword}
                  <button
                    aria-label={`Remove ${keyword}`}
                    className="p-0.5 text-[#77787A] hover:text-[#212327] dark:hover:text-white"
                    onClick={() => {
                      handleRemoveKeyword(keyword);
                    }}
                    type="button"
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                className="min-w-[120px] flex-1 bg-transparent py-0.5 text-sm text-[#212327] outline-none placeholder:text-[#9E9E9E] dark:text-white dark:placeholder:text-[#77787A]"
                onBlur={handleAddKeywords}
                onChange={(e) => {
                  setKeywordInput(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    handleAddKeywords();
                  } else if (
                    e.key === "Backspace" &&
                    !keywordInput &&
                    image.keywords.length
                  ) {
                    handleRemoveKeyword(
                      image.keywords[image.keywords.length - 1],
                    );
                  }
                }}
                placeholder={
                  image.keywords.length ? "Add…" : "tig welding, pipe…"
                }
                value={keywordInput}
              />
            </div>
            <span className="text-[11px] text-[#77787A]">
              Searchable. Enter or a comma adds one.
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <PanelLabel>Industries</PanelLabel>
            <div className="flex flex-wrap gap-1.5">
              {Object.values(ExploreImageIndustry).map((industry) => (
                <ExploreImageChip
                  key={industry}
                  label={exploreImageIndustryToLabel[industry]}
                  onClick={() => {
                    handleToggleIndustry(industry);
                  }}
                  size="sm"
                  tone={
                    image.industries.includes(industry) ? "active" : "outline"
                  }
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <PanelLabel>Credit</PanelLabel>
            {image.attribution ? (
              <>
                <p className="text-sm text-[#212327] dark:text-[#EAEAEA]">
                  {formatExploreImageCredit(image.attribution)}
                </p>
                <div className="flex flex-wrap gap-4">
                  {image.attribution.licenseUrl && (
                    <PanelLink href={image.attribution.licenseUrl}>
                      Licence
                    </PanelLink>
                  )}
                  {image.attribution.sourceUrl && (
                    <PanelLink href={image.attribution.sourceUrl}>
                      Source
                    </PanelLink>
                  )}
                </div>
                {image.attribution.text && (
                  <details className="group text-xs text-[#4C4F52] dark:text-[#9E9E9E]">
                    <summary className="cursor-pointer select-none font-plex-mono text-[10px] uppercase tracking-[0.16em] text-[#77787A] hover:text-[#212327] dark:hover:text-white">
                      Full attribution
                    </summary>
                    <p className="mt-2 whitespace-pre-line break-words leading-relaxed">
                      {image.attribution.text}
                    </p>
                  </details>
                )}
              </>
            ) : (
              <span className="text-sm italic text-[#77787A]">
                No credit required
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <PanelLabel>Specs</PanelLabel>
            <dl className="grid grid-cols-2 gap-px bg-black/10 font-plex-mono text-xs dark:bg-white/10">
              {[
                [
                  "Size",
                  `${image.width.toString()} × ${image.height.toString()}`,
                ],
                ["File", `${formatExploreImageSize(image.sizeBytes)} · WebP`],
                [
                  "Shape",
                  isPortraitExploreImage(image) ? "Portrait" : "Landscape",
                ],
                ["Focus", `${focus.x.toString()} · ${focus.y.toString()}`],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="flex flex-col gap-1 bg-white p-3 dark:bg-[#141518]"
                >
                  <dt className="text-[10px] uppercase tracking-[0.16em] text-[#77787A]">
                    {label}
                  </dt>
                  <dd className="text-[#212327] dark:text-[#EAEAEA]">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
            {isLowResExploreImage(image) && (
              <ExploreImageChip
                className="self-start"
                label="Low res · may look soft on 3x phones"
                size="sm"
                tone="copper"
              />
            )}
          </div>

          <div className="flex flex-col gap-3">
            <PanelLabel>In the app</PanelLabel>
            <div className="flex flex-wrap gap-1.5">
              {PREVIEW_TABS.map((tab) => (
                <ExploreImageChip
                  key={tab.value}
                  label={tab.label}
                  onClick={() => {
                    setPreviewTab(tab.value);
                  }}
                  tone={previewTab === tab.value ? "active" : "default"}
                />
              ))}
            </div>
            {renderPreview()}
          </div>

          <button
            className="mt-auto flex items-center justify-center gap-2 py-2.5 font-plex-mono text-[11px] uppercase tracking-[0.16em] text-red-600 ring-1 ring-inset ring-red-600/30 transition-colors hover:bg-red-500/10 dark:text-red-400 dark:ring-red-400/30"
            onClick={() => {
              setIsDeleteDialogOpen(true);
            }}
            style={{ clipPath: chamferClipPath(8) }}
            type="button"
          >
            <Trash2Icon className="h-4 w-4" />
            Delete image
          </button>
        </div>
      </div>

      <ConfirmationDialog
        confirmButtonText="Delete"
        confirmationButtonVariant="destructive"
        description="This removes the image from every category in the worker app."
        errorMessage="Failed to delete the image."
        isError={deleteExploreImage.isError}
        isLoading={deleteExploreImage.isPending}
        onConfirm={handleDelete}
        onOpenChange={setIsDeleteDialogOpen}
        open={isDeleteDialogOpen}
        title="Delete image?"
      />
    </div>
  );
};

/**
 * Full-screen view of one image within the current gallery results, with
 * ← / → to step through them and Esc to close.
 */
export const ExploreImageLightbox = ({
  images,
  onSelect,
  selectedId,
}: {
  images: ExploreImage[];
  onSelect: (id: string | null) => void;
  selectedId: string | null;
}) => {
  const index = selectedId
    ? images.findIndex((image) => image.id === selectedId)
    : -1;
  const image = index === -1 ? null : images[index];

  const selectOffset = (offset: number) => {
    if (!images.length) return;
    const next = (index + offset + images.length) % images.length;
    onSelect(images[next].id);
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        if (!open) onSelect(null);
      }}
      open={!!image}
    >
      {/* Overrides the dialog's centred card: full-bleed, no padding or gap,
          and a block box so the base `grid gap-4` adds no height. */}
      <DialogContent
        aria-describedby={undefined}
        className="left-0 top-0 block h-dvh w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-y-auto border-0 bg-[#FAFAFA] p-0 text-[#212327] data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0 data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 dark:bg-[#0E0F11] dark:text-[#FAFAFA] sm:rounded-none lg:overflow-hidden"
        hideCloseButton
      >
        {image && (
          <LightboxBody
            key={image.id}
            image={image}
            index={index}
            onClose={() => {
              onSelect(null);
            }}
            onDeleted={() => {
              // Move on to the next image, or close if that was the last one.
              const next = images[(index + 1) % images.length];
              onSelect(images.length > 1 ? next.id : null);
            }}
            onNext={() => {
              selectOffset(1);
            }}
            onPrevious={() => {
              selectOffset(-1);
            }}
            total={images.length}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
